import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  settingsOutboundWebhooksWebhooksCreateMutation,
  settingsOutboundWebhooksWebhooksDestroyMutation,
  settingsOutboundWebhooksWebhooksListOptions,
  settingsOutboundWebhooksWebhooksListQueryKey,
  settingsOutboundWebhooksWebhooksPartialUpdateMutation,
  settingsOutboundWebhooksWebhooksRevealSecretCreateMutation,
  settingsOutboundWebhooksWebhooksTestCreateMutation
} from 'src/api/main'
import type {
  OutboundWebhookRead,
  OutboundWebhookTestResult
} from 'src/api/main'
import { client } from 'src/api/main/client.gen'
import { useWorkspaceAppsList } from 'src/features/apps/hooks/useWorkspaceAppsList'
import { useWorkspaceDomainName } from 'src/features/apps/hooks/useWorkspaceDomainName'
import { buildAppLaunchUrl } from 'src/features/apps/utils/appLaunchUrl'
import { useShowApiErrors } from 'src/utils'

import { SQUAD_APP_TYPE, squadAgentUrl } from './utils'

/** One entity's events, as CRM groups them. */
export type EventGroup = {
  entity: string
  group: string
  events: { key: string; label: string }[]
}

const CATALOGUE_PATH = 'webhook/event-catalogue'
const CATALOGUE_STALE_MS = 5 * 60 * 1000

export const useOutboundWebhookList = (applicationId: number) =>
  useQuery({
    ...settingsOutboundWebhooksWebhooksListOptions({
      path: { application_id: applicationId }
    }),
    enabled: applicationId > 0,
    // Opt out of the app-wide refetchOnMount:false. Deleting from the detail page invalidates this
    // list while it is unmounted, and an invalidated inactive query does not refetch on mount --
    // so the deleted row came back when navigating to the list.
    refetchOnMount: 'always'
  })

/**
 * Mutations only, deliberately separate from the list.
 *
 * The detail page needs to edit, delete and reveal but renders a single record, so bundling the
 * list query in here made it fetch every webhook for the application and throw the result away.
 */
export const useOutboundWebhookMutations = (applicationId: number) => {
  const queryClient = useQueryClient()

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: settingsOutboundWebhooksWebhooksListQueryKey({
        path: { application_id: applicationId }
      })
    })

  const create = useMutation({
    ...settingsOutboundWebhooksWebhooksCreateMutation(),
    onSuccess: invalidate
  })

  const update = useMutation({
    ...settingsOutboundWebhooksWebhooksPartialUpdateMutation(),
    onSuccess: invalidate
  })

  const remove = useMutation({
    ...settingsOutboundWebhooksWebhooksDestroyMutation(),
    onSuccess: invalidate
  })

  // Not invalidating on success: reveal reads a value the list never carries, so there is
  // nothing in the cache it could have changed.
  const reveal = useMutation({
    ...settingsOutboundWebhooksWebhooksRevealSecretCreateMutation()
  })

  return { create, update, remove, reveal }
}

/**
 * The Test button, shared by the list and the detail page.
 *
 * Both render the same modal from the same mutation, and both had their own copy of the handler
 * and its state. The result is transient UI state rather than anything the cache holds, so
 * nothing is invalidated: a test delivers to the customer's endpoint and stores nothing here.
 */
export const useWebhookTest = (applicationId: number) => {
  const showApiErrors = useShowApiErrors()
  const [result, setResult] = useState<OutboundWebhookTestResult | null>(null)

  const mutation = useMutation({
    ...settingsOutboundWebhooksWebhooksTestCreateMutation()
  })

  const send = async (id: number) => {
    try {
      // A refused URL or an unreachable host comes back as a 200 with ok:false, so only a real
      // API failure lands in the catch.
      setResult(
        await mutation.mutateAsync({
          path: { application_id: applicationId, id }
        })
      )
    } catch (error) {
      showApiErrors(error)
    }
  }

  return { result, send, close: () => setResult(null), mutation }
}

/**
 * The event list for the selector, fetched from the CRM instance itself.
 *
 * Hand-written rather than generated, which the codegen rule in AGENTS.md otherwise forbids:
 * that rule governs Core endpoints, and this is a second service with no generated client. The
 * list lives in CRM because CRM is what emits the events, so it cannot drift from what actually
 * fires -- and a new event appears here with no deployment on either side.
 *
 * It still goes through the generated client's axios instance rather than `fetch`, because that
 * instance carries the interceptors: the session token is attached for us, and a request that
 * fails on an expired token is retried after a silent refresh. A bare `fetch` skipped both, so a
 * tab left open past the token lifetime reported the CRM app as unreachable.
 *
 * The URL comes from the application record, not an env var, so it is correct per organization.
 * CRM accepts the same `Token` scheme Core does, and the account token identifies which CRM
 * account is asking.
 */
export const useEventCatalogue = ({
  domain,
  appToken,
  enabled
}: {
  domain: string | undefined
  appToken: string | undefined
  enabled: boolean
}) => {
  const { i18n } = useTranslation()

  return useQuery<EventGroup[]>({
    queryKey: ['crm-event-catalogue', domain, appToken, i18n.language],
    enabled: enabled && Boolean(domain) && Boolean(appToken),
    staleTime: CATALOGUE_STALE_MS,
    queryFn: async () => {
      const { data } = await client.instance.get<EventGroup[]>(
        `${(domain ?? '').replace(/\/+$/, '')}/${CATALOGUE_PATH}`,
        {
          // Absolute URL, so the client's own baseURL must not be prepended.
          baseURL: '',
          headers: {
            'SAAS-APP-TOKEN': appToken ?? '',
            'Accept-Language': i18n.language
          }
        }
      )

      return data
    }
  })
}

/**
 * Builds the "Open in Squad" link for a webhook Squad manages.
 *
 * The page lives in the workspace's own Squad app, so the base is that app's launch URL --
 * the same one the apps board opens -- not the Squad API host Account talks to. Both queries
 * are the cached ones the apps pages already run.
 */
export const useSquadAgentUrl = (workspaceId: number) => {
  const { apps } = useWorkspaceAppsList(workspaceId)
  const domainName = useWorkspaceDomainName(workspaceId)
  const squadApp = apps.find((app) => app.instanceTypeCode === SQUAD_APP_TYPE)
  const launchUrl = squadApp ? buildAppLaunchUrl(squadApp, domainName) : null

  return (webhook: Pick<OutboundWebhookRead, 'managed_by' | 'managed_ref'>) =>
    squadAgentUrl(launchUrl, webhook)
}
